import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { collection, deleteDoc, doc, getDocs, orderBy, query } from 'firebase/firestore';
import { toast } from 'sonner';
import { Link2, Plus, Trash2 } from 'lucide-react';

import { db } from '../../../firebase/config';
import { backfillTournamentSlugs } from '../../../database/tournaments';
import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';
import { Card, CardContent, CardFooter } from '../../ui/card';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../../ui/dialog';
import { cn } from '../../../lib/utils';
import PageHeader from '../shell/PageHeader';
import { FORMAT_LABELS, isLegacyTournament } from '../shell/tournamentTools';
import { PUBLIC_SECTIONS } from '../../../lib/stages';
import { gameLabel, modeLabel } from '../../../constants/modes';

import TournamentSettingsForm from './TournamentSettingsForm';

const toDate = (v) => (v && typeof v.toDate === 'function' ? v.toDate() : v ? new Date(v) : null);

const fmtDateRange = (start, end) => {
  const f = (d) =>
    d
      ? d
          .toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' })
          .toUpperCase()
          .replace(/\./g, '')
      : '—';
  return `${f(start)} → ${f(end)}`;
};

const statusPill = {
  active: 'border-heat/60 text-heat',
  upcoming: 'border-border text-foreground',
  completed: 'border-border/60 text-muted-foreground',
};

const calcStatus = (t) => {
  const now = new Date();
  const start = toDate(t.startDate);
  const end = toDate(t.endDate);
  if (start && now < start) return 'upcoming';
  if (end && now > end) return 'completed';
  return 'active';
};

const TournamentsListPage = () => {
  const navigate = useNavigate();
  const [tournaments, setTournaments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [backfilling, setBackfilling] = useState(false);

  const missingSlugs = tournaments.filter((t) => !t.slug).length;

  const handleBackfillSlugs = async () => {
    setBackfilling(true);
    try {
      const updated = await backfillTournamentSlugs();
      toast.success(
        updated > 0
          ? `Added slugs to ${updated} tournament${updated === 1 ? '' : 's'}.`
          : 'All tournaments already have a slug.'
      );
      await fetchTournaments();
    } catch (error) {
      toast.error('Backfill failed.', { description: error.message });
    } finally {
      setBackfilling(false);
    }
  };

  const fetchTournaments = async () => {
    try {
      setLoading(true);
      const q = query(collection(db, 'tournaments'), orderBy('startDate', 'desc'));
      const snap = await getDocs(q);
      setTournaments(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    } catch (error) {
      toast.error('Could not load tournaments.', { description: error.message });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTournaments();
  }, []);

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteDoc(doc(db, 'tournaments', deleteTarget.id));
      toast.success('Tournament deleted.');
      setDeleteTarget(null);
      await fetchTournaments();
    } catch (error) {
      toast.error('Delete failed.', { description: error.message });
    }
  };

  return (
    <div className="pb-16">
      <PageHeader
        eyebrow="Tournaments"
        title="Tournaments"
        description="Click a tournament to open its admin page. Create a new one to get started."
        actions={
          <div className="flex items-center gap-2">
            {missingSlugs > 0 && (
              <Button variant="outline" onClick={handleBackfillSlugs} disabled={backfilling}>
                <Link2 className="h-4 w-4" />
                {backfilling
                  ? 'Generating…'
                  : `Generate ${missingSlugs} missing slug${missingSlugs === 1 ? '' : 's'}`}
              </Button>
            )}
            <Button onClick={() => setCreateOpen(true)}>
              <Plus className="h-4 w-4" />
              New tournament
            </Button>
          </div>
        }
      />

      {loading ? (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 4 }).map((_, i) => (
            <div
              key={i}
              className="h-64 animate-pulse rounded-md border border-border/60 bg-muted/10"
            />
          ))}
        </div>
      ) : tournaments.length === 0 ? (
        <div className="border border-dashed border-border/60 px-6 py-16 text-center">
          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
            None yet
          </p>
          <p className="mt-3 text-sm text-muted-foreground">
            Create your first tournament to get started.
          </p>
          <div className="mt-5">
            <Button onClick={() => setCreateOpen(true)}>
              <Plus className="h-4 w-4" />
              New tournament
            </Button>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {tournaments.map((t) => {
            const status = calcStatus(t);
            return (
              <Card
                key={t.id}
                className="group overflow-hidden border-border/60 bg-background/40 transition-colors hover:border-border"
              >
                <Link to={`/admin/tournaments/${t.id}`} className="block">
                  {t.imageUrl ? (
                    <div className="relative h-40 w-full overflow-hidden border-b border-border/60">
                      <img
                        src={t.imageUrl}
                        alt=""
                        className="h-full w-full object-cover opacity-80 transition-opacity group-hover:opacity-100"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-background/80 via-background/20 to-transparent" />
                    </div>
                  ) : (
                    <div className="h-3 w-full bg-gradient-to-r from-heat/40 via-primary/20 to-transparent" />
                  )}
                  <CardContent className="space-y-3 pt-5">
                    <div className="flex items-start justify-between gap-3">
                      <h3 className="truncate font-display text-xl font-semibold tracking-tight">
                        {t.name}
                      </h3>
                      <span
                        className={cn(
                          'shrink-0 border px-2 py-0.5 font-mono text-[10px] uppercase tracking-[0.2em]',
                          statusPill[status] || statusPill.upcoming
                        )}
                      >
                        {status}
                      </span>
                    </div>
                    <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground tabular-nums">
                      {fmtDateRange(toDate(t.startDate), toDate(t.endDate))}
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {isLegacyTournament(t) ? (
                        <Badge variant="outline" className="text-muted-foreground">
                          Legacy · {FORMAT_LABELS[t.format] || t.format || 'unset'}
                        </Badge>
                      ) : (
                        <>
                          <Badge variant="outline">{FORMAT_LABELS.staged}</Badge>
                          {!PUBLIC_SECTIONS.some(({ key }) => t.visibility?.[key] === true) && (
                            <Badge variant="outline">Rules only</Badge>
                          )}
                        </>
                      )}
                      {t.game && <Badge variant="outline">Game · {gameLabel(t.game)}</Badge>}
                      {t.mode && <Badge variant="outline">Mode · {modeLabel(t.mode)}</Badge>}
                      {t.enableDrinkCounter && <Badge variant="outline">Drinks on</Badge>}
                    </div>
                    {t.description && (
                      <p className="line-clamp-2 text-sm text-muted-foreground">{t.description}</p>
                    )}
                  </CardContent>
                </Link>
                <CardFooter className="justify-end gap-2 border-t border-border/60 pt-4">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-destructive hover:text-destructive"
                    onClick={(e) => {
                      e.preventDefault();
                      setDeleteTarget(t);
                    }}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                    Delete
                  </Button>
                </CardFooter>
              </Card>
            );
          })}
        </div>
      )}

      {/* Create modal */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="!max-w-5xl !max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>New tournament</DialogTitle>
          </DialogHeader>
          <TournamentSettingsForm
            existing={null}
            onCancel={() => setCreateOpen(false)}
            onSaved={(saved) => {
              setCreateOpen(false);
              if (saved?.id) navigate(`/admin/tournaments/${saved.id}`);
              else fetchTournaments();
            }}
          />
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <Dialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Delete tournament?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            This permanently removes{' '}
            <span className="font-medium text-foreground">{deleteTarget?.name}</span> and cannot be
            undone. Players, maps, and any recorded results stay in Firestore but will be orphaned.
          </p>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDeleteTarget(null)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete}>
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TournamentsListPage;
